import { useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import type { ScreenConfig } from "../shared/protocol.ts";
import { Dashboard } from "./components/Dashboard.tsx";
import { IdleScreen } from "./components/IdleScreen.tsx";
import { ImageScreen } from "./components/ImageScreen.tsx";
import { StatusDot } from "./components/StatusDot.tsx";
import { VideoScreen } from "./components/VideoScreen.tsx";
import { WebScreen } from "./components/WebScreen.tsx";
import { CallScreen } from "./components/CallScreen.tsx";
import { useDisplay } from "./useDisplay.ts";

/** Presentation branding is RAFED; language direction remains configurable. */
function useDirection(config: ScreenConfig) {
  useEffect(() => {
    const root = document.documentElement;
    // `auto` means "let each string decide", which is right for a bilingual
    // room; removing the attribute rather than writing "auto" keeps the
    // per-element dir="auto" the components already set in charge.
    if (config.direction === "auto") root.removeAttribute("dir");
    else root.setAttribute("dir", config.direction);
  }, [config.direction]);
}

export default function App() {
  const { state, connected } = useDisplay();
  useDirection(state.config);

  return (
    <div className="h-full w-full">
      {state.config.showStatus ? (
        <StatusDot connected={connected} robotOnline={state.robot.online} />
      ) : null}

      {/* One mode on screen at a time; the crossfade hides the swap. */}
      <AnimatePresence mode="wait">
        {state.mode === "call" && state.call ? (
          <motion.div key="call" className="h-full" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <CallScreen call={state.call} speaking={state.robot.speaking} connected={connected && state.robot.online} />
          </motion.div>
        ) : state.mode === "video" && state.video ? (
          <motion.div key="video" className="h-full">
            <VideoScreen key={state.video.playbackId || state.video.url}
              url={state.video.url} title={state.video.title} forceMuted={state.video.muted} />
          </motion.div>
        ) : state.mode === "page" && state.page ? (
          <motion.div key="page" className="h-full">
            <WebScreen url={state.page.url} title={state.page.title} />
          </motion.div>
        ) : state.mode === "image" && state.image ? (
          <motion.div key="image" className="h-full">
            <ImageScreen url={state.image.url} title={state.image.title} />
          </motion.div>
        ) : state.mode === "dashboard" && state.tiles.length > 0 ? (
          <motion.div
            key="dashboard"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="h-full"
          >
            <Dashboard
              title={state.title || state.config.dashboardFallbackTitle}
              tiles={state.tiles}
            />
          </motion.div>
        ) : (
          <motion.div key="idle" exit={{ opacity: 0 }} className="h-full">
            <IdleScreen config={state.config} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
